import { useCallback, useEffect, useState } from 'react';
import { fetchMyVisits, type ProfileVisit } from '../socialApi';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { HopperIcon } from '../ui/icons/HopperIcon';
import { Skeleton } from '../ui/Skeleton';

type BackProps = { onBack: () => void };

type SavedPlaceItem = {
  venue: {
    id: string;
    name: string | null;
    city: { id: string; name: string };
    cover_url: string | null;
  };
  note: string | null;
  saved_at: string | null;
  friends_who_saved_count: number;
};

type SavedPlacesResponse = {
  items: SavedPlaceItem[];
  pagination: { limit: number; has_more: boolean; next_cursor: string | null };
};

async function fetchSavedPlaces(cursor: string | null = null): Promise<SavedPlacesResponse> {
  const params = new URLSearchParams({ limit: '20' });
  if (cursor) params.set('cursor', cursor);
  const response = await fetch(`/api/v1/me/saved-places?${params.toString()}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Saved places request failed: ${response.status}`);
  return response.json() as Promise<SavedPlacesResponse>;
}

function ScreenHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <header className="pulse-aux-header">
      <Button variant="ghost" size="sm" leadingIcon={<HopperIcon name="arrow-left" size={18} />} onClick={onBack}>Назад</Button>
      <h1>{title}</h1>
    </header>
  );
}

function formatVisitDate(value: string): string {
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

export function PulseVisitsScreen({ onBack, onOpenVenue }: BackProps & { onOpenVenue: (venueId: string) => void }) {
  const [items, setItems] = useState<ProfileVisit[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async (append = false) => {
    setLoading(true);
    setError(false);
    try {
      const response = await fetchMyVisits('all', 20, append ? cursor : null);
      setItems((current) => append ? [...current, ...response.visits.items] : response.visits.items);
      setCursor(response.visits.pagination.next_cursor);
      setHasMore(response.visits.pagination.has_more);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [cursor]);

  useEffect(() => { void load(false); /* initial fetch */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="pulse-aux-screen">
      <ScreenHeader title="Мои визиты" onBack={onBack} />
      {loading && items.length === 0 ? <div className="pulse-aux-list">{Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={96} />)}</div>
        : error ? <div className="pulse-empty"><strong>Не удалось загрузить визиты</strong><Button variant="outline" size="sm" onClick={() => void load(false)}>Повторить</Button></div>
          : items.length === 0 ? <div className="pulse-empty"><HopperIcon name="bookmark" size={28} /><strong>Подтверждённых визитов пока нет</strong></div>
            : <div className="pulse-aux-list">{items.map((visit) => (
              <Card className="pulse-aux-card" key={visit.id}>
                <button type="button" onClick={() => onOpenVenue(visit.venue.id)}>
                  <span className="pulse-aux-card__icon"><HopperIcon name="pin" size={20} /></span>
                  <span><strong>{visit.venue.name?.trim() || 'Место Хоппера'}</strong><small>{visit.city.name} · {formatVisitDate(visit.local_date)}</small></span>
                  <span className="pulse-aux-card__metric">+{visit.xp} XP</span>
                </button>
              </Card>
            ))}</div>}
      {hasMore && !loading && <Button variant="outline" className="pulse-load-more" onClick={() => void load(true)}>Показать ещё</Button>}
    </main>
  );
}

export function PulseSavedPlacesScreen({ onBack, onOpenVenue }: BackProps & { onOpenVenue: (venueId: string) => void }) {
  const [items, setItems] = useState<SavedPlaceItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async (append = false) => {
    setLoading(true);
    setError(false);
    try {
      const response = await fetchSavedPlaces(append ? cursor : null);
      setItems((current) => append ? [...current, ...response.items] : response.items);
      setCursor(response.pagination.next_cursor);
      setHasMore(response.pagination.has_more);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [cursor]);

  useEffect(() => { void load(false); /* initial fetch */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="pulse-aux-screen">
      <ScreenHeader title="Избранное" onBack={onBack} />
      {loading && items.length === 0 ? <div className="pulse-aux-list">{Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={110} />)}</div>
        : error ? <div className="pulse-empty"><strong>Не удалось загрузить избранное</strong><Button variant="outline" size="sm" onClick={() => void load(false)}>Повторить</Button></div>
          : items.length === 0 ? <div className="pulse-empty"><HopperIcon name="heart" size={28} /><strong>Сохранённых мест пока нет</strong><span>Добавляйте места в избранное из карточки заведения.</span></div>
            : <div className="pulse-aux-list">{items.map((item) => (
              <Card className="pulse-aux-card pulse-aux-card--saved" key={item.venue.id}>
                <button type="button" onClick={() => onOpenVenue(item.venue.id)}>
                  {item.venue.cover_url ? <img src={item.venue.cover_url} alt="" /> : <span className="pulse-aux-card__cover"><HopperIcon name="pin" size={22} /></span>}
                  <span><strong>{item.venue.name?.trim() || 'Место Хоппера'}</strong><small>{item.venue.city.name}</small>{item.note && <em>{item.note}</em>}</span>
                  <HopperIcon name="chevron-right" size={18} />
                </button>
              </Card>
            ))}</div>}
      {hasMore && !loading && <Button variant="outline" className="pulse-load-more" onClick={() => void load(true)}>Показать ещё</Button>}
    </main>
  );
}

export function PulseRoutesSoonScreen({ onBack }: BackProps) {
  return (
    <main className="pulse-aux-screen">
      <ScreenHeader title="Маршруты" onBack={onBack} />
      <Card className="pulse-routes-soon">
        <span><HopperIcon name="routes" size={34} /></span>
        <h2>Маршруты скоро появятся</h2>
        <p>Мы готовим маршруты по реальным местам города. Сейчас этот раздел ещё не запущен.</p>
      </Card>
    </main>
  );
}
